<template>
  <component
    :is="layoutComponent"
    v-bind="layoutProps">
    <template slot="field">
      <div class="custom-file">
        <input
          :id="field.attribute"
          ref="input"
          :class="errorClasses"
          :multiple="field.multiple || false"
          :accept="field.mimeTypes"
          :disabled="isDisabled"
          type="file"
          class="custom-file-input"
          v-bind="field.attrs"
          @input="input($event)">
        <label
          :for="field.attribute"
          class="custom-file-label">{{ field.placeholder || strings.file.placeholder }}</label>
      </div>
      <div
        v-if="hasAttached && field.multiple">
        <draggable
          v-model="sortables"
          :options="draggableOptions"
          class="field-attachments mt-4">
          <div
            v-for="attachment in field.attached"
            :key="attachment.id"
            class="field-attachment field-attachment-draggable bg-light rounded p-2">
            <attachment-preview
              :attachment="attachment"
              :options="previewOptions" />
            <button
              v-if="!isDisabled"
              type="button"
              class="close rounded btn-remove bg-secondary"
              @click.prevent="remove(attachment)">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        </draggable>
        <button
          v-if="hasAttached && field.multiple && !isDisabled"
          type="button"
          class="btn btn-sm btn-warning"
          @click.stop.prevent="clear()">{{ strings.attachment.clear }}</button>
      </div>
      <div
        v-if="hasAttached && !field.multiple && fieldValue">
        <div
          class="field-attachments mt-4">
          <div class="field-attachment bg-light rounded p-2">
            <attachment-preview
              v-if="typeof field.attached === 'object'"
              :attachment="field.attached"
              :options="previewOptions"/>
            <span v-else>{{fieldValue && fieldValue.filename ? `${fieldValue.filename}.${fieldValue.extension}` : ''}}</span>
            <button
              v-if="!isDisabled"
              type="button"
              class="close rounded btn-remove bg-secondary"
              @click.stop.prevent="clear()">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        </div>
      </div>
    </template>
  </component>
</template>

<script>
import draggable from 'vuedraggable';
import assign from 'lodash-es/assign';
import FieldMixin from '../mixins/FieldMixin';
import AttachmentPreview from './AttachmentPreview.vue';
import pick from 'lodash-es/pick';

function fileToAttached(file) {
  return {
    id: Math.random(),
    file,
    filename: file.name,
    mime_type: file.type,
    size: file.size,
  };
}

export default {
  components: {
    draggable,
    AttachmentPreview,
  },

  mixins: [FieldMixin],

  computed: {
    hasAttached() {
      return this.field.multiple ?
        !! this.field.attached.length :
        !! this.field.attached;
    },
    draggableOptions() {
      const options = { direction: 'horizontal' };
      assign(options, this.field.draggableOptions);
      return options;
    },
    previewOptions() {
      return pick(this.field, [
        'previewMaxWidth',
        'previewMaxHeight',
        'previewMinWidth',
        'previewMinHeight',
        'previewCrop',
      ]);
    },
    sortables: {
      get() {
        return this.field.attached;
      },
      set(sorted) {
        this.field.attached = sorted;
        this.fieldValue = this.field.attached.map((attachment) => {
          return attachment.file ? attachment.file : attachment.id;
        });
      }
    },
  },

  created() {
    if (this.field.multiple) {
      // For correct reactivity and sorting
      if (!this.field.attached) {
        this.field.attached = [];
      }

      if (!this.fieldValue) {
        this.fieldValue = [];
      }
    }
  },

  methods: {
    remove(attachment) {
      if (this.field.multiple) {
        this.field.attached = this.field.attached.filter((a) => a.id !== attachment.id);
        this.fieldValue = this.fieldValue.filter((id) => {
          if (attachment.file) {
            return id !== attachment.file;
          }

          return id !== attachment.id
        });
      } else {
        this.field.attached = null;
        this.fieldValue = null;
      }
    },
    clear() {
      if (this.field.multiple) {
        this.field.attached = [];
        this.fieldValue = [];
      } else {
        this.field.attached = null;
        this.fieldValue = null;
      }
    },
    input($event) {
      const files = [];

      for (let i = 0; i < $event.target.files.length; i += 1) {
        files.push($event.target.files[i]);
      }

      this.field.attached = this.field.multiple ?
        this.field.attached.concat(files.map(fileToAttached)) :
        fileToAttached(files[0]);

      this.fieldValue = this.field.multiple ? this.fieldValue.concat(files) : files[0];

      this.$refs.input.value = '';
    },
  },
};
</script>

<style lang="scss" scoped>
  .field-attachments {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
  }

  .field-attachment {
    position: relative;
    margin-right: 1rem;
    margin-bottom: 1rem;
  }

  .btn-remove {
    position: absolute;
    top: -.5rem;
    right: -.5rem;
    line-height: 100%;
    height: 1.5rem;
    width: 1.5rem;
    opacity: 1;
    color: white;
  }

  .field-attachment-preview {
    font-size: 85%;
  }

  .field-attachment-draggable {
    cursor: move;
  }
</style>
